import {
  Column,
  Columns,
  Stack,
  StackSection,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import React from "react";
import { useAppContext } from "../../context/AppContext";
import styled from "styled-components";
import { useOffercode } from "../../hooks/useOffercode";
import { IDeliverabilityStatus } from "../../model/IDeliverability";
import { OfferType } from "../Offers/offerTab";
import content from "./content.json";

export const STANDARD_EXCHANGE_OFFER_DISCLAIMERS =
  content.disclaimers.standard.exchange;
export const STANDARD_REFILL_OFFER_DISCLAIMERS =
  content.disclaimers.standard.refill;
export const DISCOUNTED_EXCHANGE_OFFER_DISCLAIMERS =
  content.disclaimers.discounted.exchange;
export const DISCOUNTED_REFILL_OFFER_DISCLAIMERS =
  content.disclaimers.discounted.refill;
export const LPGBUNDLE_EXCHANGE_DISCLAIMERS =
  content.disclaimers.lpgbundleoffer.exchange;
export const LPGBUNDLE_REFILL_DISCLAIMERS =
  content.disclaimers.lpgbundleoffer.refill;
export const LPGMOVEOFFERS_EXCHANGE_DISCLAIMERS =
  content.disclaimers.lpgmoveoffer.exchange;
export const LPGMOVEOFFERS_REFILL_DISCLAIMERS =
  content.disclaimers.lpgmoveoffer.refill;

// Aliases used by tests
export const OFFER_DISCLAIMERS = STANDARD_EXCHANGE_OFFER_DISCLAIMERS;
export const LPGBUNDLE_DISCLAIMERS = LPGBUNDLE_EXCHANGE_DISCLAIMERS;

const OFFER_CODE_DISCLAIMER_MAP: {
  [key: string]: { exchange: string[]; refill: string[] };
} = {
  lpgbundleoffer: {
    exchange: LPGBUNDLE_EXCHANGE_DISCLAIMERS,
    refill: LPGBUNDLE_REFILL_DISCLAIMERS,
  },
  lpgmoveoffer: {
    exchange: LPGMOVEOFFERS_EXCHANGE_DISCLAIMERS,
    refill: LPGMOVEOFFERS_REFILL_DISCLAIMERS,
  },
  movers50: {
    exchange: LPGMOVEOFFERS_EXCHANGE_DISCLAIMERS,
    refill: LPGMOVEOFFERS_REFILL_DISCLAIMERS,
  },
};

const StyledStack = styled(Stack)`
  margin-top: ${(props) => props.theme.space.xlarge}px;

  ${(props) => props.theme.breakpoints.up("lg")} {
    margin-top: ${(props) => props.theme.space.xxlarge}px;
  }
`;

type OfferDisclaimerProps = {
  offerType: OfferType;
};

export const OfferDisclaimer = ({ offerType }: OfferDisclaimerProps) => {
  const { context } = useAppContext();
  const { loading } = context;
  const { offers, deliverability } = { ...context?.offersData };

  const offerCode = useOffercode()?.toLowerCase();

  const offerCodeMap = offerCode ? OFFER_CODE_DISCLAIMER_MAP[offerCode] : null;
  const hasUnknownOfferCode = !!offerCode && !offerCodeMap;
  const isDiscounted =
    !offerCode &&
    offers?.some((offer) => {
      const isRefill = offerType === OfferType.REFILL;
      return (
        offer.isRefill === isRefill &&
        offer.bottlePricing?.["1"] &&
        Number(offer.bottlePricing?.["1"].listPrice) >
          Number(offer.bottlePricing?.["1"].price)
      );
    });

  const disclaimers: (string | React.JSX.Element)[] = offerCodeMap
    ? offerType === OfferType.REFILL
      ? offerCodeMap.refill
      : offerCodeMap.exchange
    : hasUnknownOfferCode
    ? offerType === OfferType.REFILL
      ? LPGMOVEOFFERS_REFILL_DISCLAIMERS
      : LPGMOVEOFFERS_EXCHANGE_DISCLAIMERS
    : isDiscounted
    ? offerType === OfferType.REFILL
      ? DISCOUNTED_REFILL_OFFER_DISCLAIMERS
      : DISCOUNTED_EXCHANGE_OFFER_DISCLAIMERS
    : offerType === OfferType.REFILL
    ? STANDARD_REFILL_OFFER_DISCLAIMERS
    : STANDARD_EXCHANGE_OFFER_DISCLAIMERS;

  const isDeliverable = deliverability
    ? offerType === OfferType.EXCHANGE
      ? deliverability?.exchange.status === IDeliverabilityStatus.DELIVERABLE
      : deliverability?.refill.status === IDeliverabilityStatus.DELIVERABLE
    : true; // if deliverability is undefined, we assume it's the first load

  if (offers?.length === 0 || !isDeliverable || loading) {
    return null;
  }

  const isPriceInfoAvailableForOffer = offers?.some((offer) => {
    const isRefill = offerType === OfferType.REFILL;
    return (
      offer.isRefill === isRefill &&
      offer.bottlePricing?.["1"] &&
      Number(offer.bottlePricing?.["1"].price) > 0
    );
  });

  return (
    <StackSection backgroundColor="transparent" paddingY="none" width="xlarge">
      <StyledStack space="small" alignX={["left", "center"]}>
        <Columns
          collapseBelow="lg"
          space={["small", "large"]}
          alignX={["left", "center"]}
        >
          <Column width="content">
            <Text tone="neutral" variant="body" align={["left", "center"]}>
              <TextLink
                href={content.links.offerTermsAndConditions.href}
                target="_blank"
              >
                {content.links.offerTermsAndConditions.label}
              </TextLink>
            </Text>
          </Column>
          <Column width="content">
            <Text tone="neutral" variant="body" align={["left", "center"]}>
              <TextLink
                href={content.links.customerCharter.href}
                target="_blank"
              >
                {content.links.customerCharter.label}
              </TextLink>
            </Text>
          </Column>
        </Columns>

        {isPriceInfoAvailableForOffer
          ? disclaimers.map((disclaimer, index) => (
              <Text
                variant="body-small"
                tone="neutral"
                align={["left", "center"]}
                key={index}
              >
                {disclaimer}
              </Text>
            ))
          : null}
      </StyledStack>
    </StackSection>
  );
};
